import { useEffect, useMemo, useRef, useState } from "react";
import Layout from "@/components/ui/Layout";
import { useCurrency } from "@/contexts/CurrencyContext";
import ProductCard from "@/components/ui/ProductCard";
import BoxBlock from "@/components/ui/BoxBlock";
import FacetBlock from "@/components/ui/FacetBlock";
import { useTranslation } from "@/contexts/TranslationContext";
import { ChevronDown, ChevronUp } from "lucide-react";

export default function ProductsBlock({ products = [] }) {
    const { currency, formatCurrency } = useCurrency();
    const { t } = useTranslation();

    const [query, setQuery] = useState("");
    const [displayProducts, setDisplayProducts] = useState(products);
    const [loading, setLoading] = useState(false);
    const [totalResults, setTotalResults] = useState(products.length);

    const [openFacetMobile, setOpenFacetMobile] = useState(null);

    // attribute.key -> [attribute_value.key, ...]
    const [selected, setSelected] = useState({});

    // egyszerű request race protection
    const reqIdRef = useRef(0);

    useEffect(() => {
        setDisplayProducts(products);
        setTotalResults(products.length);
    }, [products]);

    const baseUrl = useMemo(() => {
        return (
            "/api/products" +
            "?sort[0]=sort_order:asc" +
            "&filters[product_status][$notIn][0]=draft" +
            "&filters[product_status][$notIn][1]=unlisted" +
            "&pagination[pageSize]=100" +
            "&populate[images][fields][0]=url" +
            "&populate[product_variants][fields][0]=name" +
            "&populate[product_variants][fields][1]=sku" +
            "&populate[product_variants][fields][2]=base_price" +
            "&populate[product_variants][populate][images][fields][0]=url" +
            "&populate[product_variants][populate][images][fields][1]=alternativeText" +
            "&populate[product_variants][populate][product_variant_stocks][fields][0]=quantity" +
            "&populate[product_variants][populate][product_variant_stocks][populate][warehouse][fields][0]=name" +
            "&populate[product_variants][populate][product_variant_stocks][populate][warehouse][fields][1]=use_for_web_stock" +
            "&populate[product_variants][populate][product_variant_attributes][fields][0]=custom_value_text" +
            "&populate[product_variants][populate][product_variant_attributes][fields][1]=custom_value_number" +
            "&populate[product_variants][populate][product_variant_attributes][fields][2]=custom_value_date" +
            "&populate[product_variants][populate][product_variant_attributes][populate][attribute][fields][0]=key" +
            "&populate[product_variants][populate][product_variant_attributes][populate][attribute][fields][1]=label" +
            "&populate[product_variants][populate][product_variant_attributes][populate][attribute][fields][2]=input_type" +
            "&populate[product_variants][populate][product_variant_attributes][populate][attribute_value][fields][0]=label" +
            "&populate[product_variants][populate][product_variant_attributes][populate][attribute_value][fields][1]=key"
        );
    }, []);

    // Faceteket az AKTUÁLIS találati listából számoljuk
    const facets = useMemo(() => {
        // attrKey -> { label, values: Map(valKey -> valLabel) }
        const acc = new Map();

        for (const p of displayProducts) {
            for (const v of p.product_variants ?? []) {
                for (const a of v.product_variant_attributes ?? []) {
                    const attrKey = a?.attribute?.key;
                    const attrLabel = a?.attribute?.label ?? attrKey;
                    const valKey = a?.attribute_value?.key;
                    const valLabel = a?.attribute_value?.label ?? valKey;
                    if (!attrKey || !valKey) continue;

                    if (!acc.has(attrKey)) acc.set(attrKey, { label: attrLabel, values: new Map() });
                    acc.get(attrKey).values.set(valKey, valLabel);
                }
            }
        }

        return Array.from(acc.entries())
            .map(([key, group]) => ({
                key,
                label: group.label,
                values: Array.from(group.values.entries())
                    .map(([k, lbl]) => ({ key: k, label: lbl }))
                    .sort((a, b) => (a.label ?? "").localeCompare(b.label ?? "")),
            }))
            .sort((a, b) => (a.label ?? "").localeCompare(b.label ?? ""));
    }, [displayProducts]);

    useEffect(() => {
        if (openFacetMobile && !facets.some((f) => f.key === openFacetMobile)) {
            setOpenFacetMobile(null);
        }
    }, [facets, openFacetMobile]);

    // Ha a szűkülés miatt eltűnnek opciók, takarítsuk ki a selected-ből
    useEffect(() => {
        const allowed = new Map(); // attrKey -> Set(valKey)
        for (const g of facets) {
            allowed.set(g.key, new Set(g.values.map((v) => v.key)));
        }

        setSelected((prev) => {
            let changed = false;
            const next = {};

            for (const [attrKey, vals] of Object.entries(prev)) {
                const allowSet = allowed.get(attrKey);
                if (!allowSet) {
                    changed = true;
                    continue;
                }
                const kept = (vals ?? []).filter((vk) => allowSet.has(vk));
                if (kept.length !== (vals ?? []).length) changed = true;
                if (kept.length) next[attrKey] = kept;
                else changed = true;
            }

            return changed ? next : prev;
        });
    }, [facets]);

    function buildSearchParams(q) {
        const trimmed = q.trim();
        if (!trimmed) return "";

        const tokens = trimmed.split(/\s+/).filter(Boolean);

        const nameAnd = tokens
            .map((tok, i) => `&filters[$and][${i}][name][$containsi]=${encodeURIComponent(tok)}`)
            .join("");

        const searchParams =
            tokens.length
                ? (
                    nameAnd.replaceAll("&filters[$and]", "&filters[$or][0][$and]") +
                    `&filters[$or][1][description][$containsi]=${encodeURIComponent(trimmed)}`
                )
                : (
                    `&filters[$or][0][name][$containsi]=${encodeURIComponent(trimmed)}` +
                    `&filters[$or][1][description][$containsi]=${encodeURIComponent(trimmed)}`
                );

        return searchParams;
    }

    function buildAttributeFilters(sel) {
        const groups = Object.entries(sel).filter(([, arr]) => Array.isArray(arr) && arr.length > 0);
        if (!groups.length) return "";

        // AND a csoportok között:
        // - attribute.key == attrKey
        // - attribute_value.key IN [..]
        return groups.map(([attrKey, valueKeys], i) => {
            const aKey = encodeURIComponent(attrKey);

            const head =
                `&filters[$and][${i}][product_variants][product_variant_attributes][attribute][key][$eq]=${aKey}`;

            const inPart = valueKeys
                .map((vk, j) =>
                    `&filters[$and][${i}][product_variants][product_variant_attributes][attribute_value][key][$in][${j}]=${encodeURIComponent(vk)}`
                )
                .join("");

            return head + inPart;
        }).join("");
    }

    async function fetchProducts({ q, sel }) {
        const requestId = ++reqIdRef.current;

        try {
            setLoading(true);

            const searchParams = buildSearchParams(q);
            const attributeParams = buildAttributeFilters(sel);

            const res = await fetch(baseUrl + searchParams + attributeParams);
            const json = await res.json();

            // ha közben jött újabb request, ezt dobjuk
            if (requestId !== reqIdRef.current) return;

            const nextData = json?.data ?? [];
            const nextTotal = json?.meta?.pagination?.total ?? nextData.length;

            setDisplayProducts(nextData);
            setTotalResults(nextTotal);
        } catch (e) {
            console.error("Product fetch error:", e);
        } finally {
            if (requestId === reqIdRef.current) setLoading(false);
        }
    }

    // Keresés gomb / Enter
    async function runSearch() {
        await fetchProducts({ q: query, sel: selected });
    }

    function clearSearch() {
        setQuery("");
        // nem állítjuk vissza automatikusan a props products-re,
        // inkább kérjük újra "üres" query+filterrel, hogy konzisztens legyen
        fetchProducts({ q: "", sel: {} });
        setSelected({});
    }

    function onKeyDown(e) {
        if (e.key === "Enter") runSearch();
    }

    // pipa toggle -> azonnali refetch
    function toggleFacet(attrKey, valueKey) {
        setSelected((prev) => {
            const next = { ...prev };
            const arr = Array.isArray(next[attrKey]) ? next[attrKey] : [];
            const has = arr.includes(valueKey);

            const newArr = has ? arr.filter((x) => x !== valueKey) : [...arr, valueKey];

            if (newArr.length) next[attrKey] = newArr;
            else delete next[attrKey];

            // IMPORTANT: a fetch-hez ezt a next-et használjuk (nem a régi state-et)
            fetchProducts({ q: query, sel: next });

            return next;
        });
    }

    const productCards = displayProducts.map((p) => {
        const v = p.product_variants?.[0];
        const rawImage = v?.images?.[0]?.url || p.images?.[0]?.url;
        const image = rawImage ? "" + rawImage : "/themes/printerwebshop/placeholder.webp";
        const basePrice = v?.base_price ?? null;

        const stock = (v?.product_variant_stocks ?? [])
            .filter((s) => s?.warehouse?.use_for_web_stock)
            .reduce((sum, s) => sum + (Number(s?.quantity) || 0), 0);

        return {
            id: p.id,
            title: p.name,
            image,
            href: "/" + p.slug,
            price: basePrice != null ? `${formatCurrency(basePrice)} ${currency?.symbol ?? "Ft"}` : null,
            stock,
        };
    });



    const leftColumn = (
        <div className="space-y-4">

            <BoxBlock
                title="Szűrők törlése"
                className=""
                onClick={() => setOpen((o) => !o)}          // header/kártya katt toggol
                iconOnClick={() => setOpen((o) => !o)}      // ikon katt toggol
            >
            </BoxBlock>

            <button
                title={
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation(); // ne fusson le a BoxBlock onClick (ha van)
                            setSelected({});
                            fetchProducts({ q: query, sel: {} });
                        }}
                        disabled={loading}
                        className="
        inline-flex items-center gap-2
        text-sm font-medium
        text-gray-700 dark:text-gray-200
        hover:underline
        disabled:opacity-60 disabled:cursor-not-allowed
      "
                    >
                        {/* opcionális ikon */}
                        <span className="inline-block">Szűrők törlése</span>
                    </button>
                }
                disabled={loading}
                onClick={undefined}
                className="
            w-full rounded-xl px-3 py-2 text-sm
            border border-gray-200 dark:border-zinc-700
            text-gray-700 dark:text-gray-200
            hover:bg-gray-50 dark:hover:bg-zinc-800
            disabled:opacity-60 disabled:cursor-not-allowed
          "
            >
                Szűrők törlése
            </button>

            {facets.length === 0 ? (
                <div className="text-sm text-gray-500">{t("ui.no_filters")}</div>
            ) : (
                <div className="space-y-3">
                    {facets.map((g) => {
                        const [open, setOpen] = useState(false);

                        return (
                            <>
                                <BoxBlock
                                    title={t(`facet.${g.key}`)}
                                    className=""
                                    headerClass="bg-gray-50 dark:bg-zinc-800 cursor-pointer"
                                    onClick={() => setOpen((o) => !o)}          // header/kártya katt toggol
                                    icon={open ? <ChevronUp /> : <ChevronDown />} // ikon vált
                                    iconOnClick={() => setOpen((o) => !o)}      // ikon katt toggol
                                >
                                    {open ? (
                                        <div className="space-y-1 px-2 py-2">
                                            <div
                                                className="
    rounded-xl border border-gray-200 dark:border-zinc-700
    bg-white/70 dark:bg-zinc-900/40
    overflow-hidden
  "
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {g.values.map((v, idx) => {
                                                    const checked = (selected[g.key] ?? []).includes(v.key);

                                                    return (
                                                        <label
                                                            key={v.key}
                                                            className={`
          flex items-center gap-3 px-3 py-2
          text-sm text-gray-700 dark:text-gray-200
          cursor-pointer select-none
          hover:bg-gray-50 dark:hover:bg-zinc-800/60
          ${idx !== 0 ? "border-t border-gray-200 dark:border-zinc-700" : ""}
        `}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                className="
            h-4 w-4 rounded
            border-gray-300 dark:border-zinc-700
            text-[#fe64db] focus:ring-[#fe64db] focus:ring-2
          "
                                                                checked={checked}
                                                                onChange={() => toggleFacet(g.key, v.key)}
                                                                disabled={loading}
                                                            />

                                                            <span className="flex-1 min-w-0 truncate">
                                                                {v.label ?? v.key}
                                                            </span>

                                                            {/* opcionális: kis “pipa” feeling checked állapotban */}
                                                            <span
                                                                className={`
            ml-2 inline-flex h-2 w-2 rounded-full
            ${checked ? "bg-[#fe64db]" : "bg-transparent"}
          `}
                                                                aria-hidden="true"
                                                            />
                                                        </label>
                                                    );
                                                })}
                                            </div>

                                        </div>
                                    ) : null}
                                </BoxBlock>
                            </>
                        );
                    })}
                </div>
            )}



        </div>
    );

    const centerColumn = (
        <section className="">
            <div className="w-full mx-auto space-y-4">
                <BoxBlock
                    title="Keresés"
                    className="px-3 py-3 space-y-3"
                    footer={
                        <div className="text-xs text-gray-500 dark:text-gray-400 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                            <span>{loading ? "Betöltés…" : `Összes találat: ${totalResults}`}</span>
                            {query && !loading && (
                                <span className="truncate sm:max-w-[50%]">„{query}”</span>
                            )}
                        </div>
                    }
                >
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                        <input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={onKeyDown}
                            placeholder="Keress névben vagy leírásban…"
                            className="
                w-full sm:flex-1
                rounded-xl
                border border-gray-200 dark:border-zinc-700
                bg-white dark:bg-zinc-900
                px-3 py-2
                text-sm text-gray-800 dark:text-gray-100
                outline-none
                focus:ring-2 focus:ring-gray-300 dark:focus:ring-zinc-700
              "
                        />

                        <div className="grid grid-cols-2 gap-2 sm:flex sm:gap-2 sm:w-auto">
                            <button
                                type="button"
                                onClick={runSearch}
                                disabled={loading}
                                className="
                  w-full sm:w-auto
                  rounded-xl px-4 py-2 text-sm font-semibold
                  bg-gray-900 text-white
                  disabled:opacity-60 disabled:cursor-not-allowed
                  hover:bg-gray-800
                  dark:bg-gray-100 dark:text-gray-900 dark:hover:bg-white
                "
                            >
                                {loading ? "…" : "Keresés"}
                            </button>

                            <button
                                type="button"
                                onClick={clearSearch}
                                className="
                  w-full sm:w-auto
                  rounded-xl px-3 py-2 text-sm
                  border border-gray-200 dark:border-zinc-700
                  text-gray-700 dark:text-gray-200
                  hover:bg-gray-50 dark:hover:bg-zinc-800
                "
                            >
                                Törlés
                            </button>
                        </div>
                    </div>
                </BoxBlock>

                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-8 gap-x-3 gap-y-6 sm:gap-x-6 sm:gap-y-6">
                    {productCards.map((p) => (
                        <ProductCard
                            key={p.id}
                            title={p.title}
                            image={p.image}
                            href={p.href}
                            price={p.price}
                            stock={p.stock}
                        />
                    ))}
                </div>
            </div>
        </section>
    );

    return <Layout left={leftColumn} center={centerColumn} />;
}
